/**
 * VELOCITY BAR CHART
 *
 * Total executions per day. Answers "how much testing happened when?"
 * Complements TrendLineChart (quality) with a volume view.
 *
 * Props:
 *   - buckets: Array<{ bucketStart, totalExecutions }>
 *   - height: number (default 200)
 */

import React, { useMemo } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts'
import { token } from '@atlaskit/tokens'

const formatDay = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null
  const { bucketStart, totalExecutions } = payload[0].payload
  return (
    <div
      style={{
        backgroundColor: token('elevation.surface.overlay', '#FFFFFF'),
        border: `1px solid ${token('color.border', '#DFE1E6')}`,
        borderRadius: '3px',
        padding: '8px 10px',
        fontSize: '12px',
        boxShadow: '0 2px 6px rgba(9, 30, 66, 0.25)'
      }}
    >
      <div style={{ fontWeight: 600, color: token('color.text', '#172B4D') }}>
        {new Date(bucketStart).toLocaleDateString(undefined, {
          weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
        })}
      </div>
      <div style={{ marginTop: '4px', color: token('color.text.subtle', '#6B778C') }}>
        Executions: <strong style={{ color: token('color.text', '#172B4D') }}>{totalExecutions}</strong>
      </div>
    </div>
  )
}

const VelocityBarChart = ({ buckets = [], height = 200 }) => {
  const data = useMemo(() => buckets || [], [buckets])

  if (!data.length) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: token('color.text.subtle', '#6B778C'),
          fontSize: '13px'
        }}
      >
        No execution volume to plot yet.
      </div>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 8 }}>
        <CartesianGrid stroke={token('color.border', '#DFE1E6')} strokeDasharray="3 3" />
        <XAxis
          dataKey="bucketStart"
          tickFormatter={formatDay}
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
        />
        <YAxis
          tick={{ fontSize: 11, fill: token('color.text.subtle', '#6B778C') }}
          stroke={token('color.border', '#DFE1E6')}
          allowDecimals={false}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: token('color.background.neutral', '#F4F5F7') }} />
        <Bar
          dataKey="totalExecutions"
          fill={token('color.background.information.bold', '#0065FF')}
          radius={[2, 2, 0, 0]}
          isAnimationActive={false}
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

export default VelocityBarChart
